import React, { useState, useEffect } from 'react';
import './PhotoAdmin.css';
import { useImageContext } from '../context/ImageContext';

const IMG_BASE = ""; // Use server root (public_html)

const folders = ['Weddings', 'Events', 'Concerts', 'Portraits', 'Pre-Shoots'];
const filterOptions = ['All Images', ...folders];

function PhotoAdmin() {
  const { images, setImages } = useImageContext();
  const [selectedFolder, setSelectedFolder] = useState(folders[0]);
  const [filterFolder, setFilterFolder] = useState('All Images');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fetchImages = async () => {
    try {
      const res = await fetch(`${IMG_BASE}/get-images.php`);
      const data = await res.json();
      setImages(data);
    } catch (err) {
      console.error("Failed to fetch images", err);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const totalFiles = files.length;
      let completed = 0;
      
      for (const file of files) {
        const formData = new FormData();
        formData.append("image", file);
        formData.append("folder", selectedFolder);
        
        await fetch(`${IMG_BASE}/upload.php`, {
          method: "POST",
          body: formData,
        });
        
        completed++;
        setUploadProgress(Math.round((completed / totalFiles) * 100));
      }
    } catch (error) {
      console.error("Error uploading images:", error);
    } finally {
      await fetchImages();
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteImage = async (imgUrl) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      const res = await fetch(`${IMG_BASE}/delete.php`, {
        method: "POST",
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ url: imgUrl })
      });

      const data = await res.json();
      if (data.success) {
        await fetchImages();
      }
    } catch (err) {
      console.error("Network error while deleting:", err);
    }
  };

  const folderStats = folders.map(folder => ({
    name: folder,
    count: images.filter(img => img.folder === folder).length,
  }));

  const filteredImages = filterFolder === 'All Images'
    ? images
    : images.filter(img => img.folder === filterFolder);

  return (
    <div className="premium-admin-container">
      <header className="admin-header">
        <div className="header-content">
          <h1>
            <span className="gradient-text">PhotoFolio</span> Admin
          </h1>
          <p className="admin-subtitle">Manage your professional photography collection</p>
        </div>
        <div className="header-stats">
          <div className="stat-card">
            <span>Total Images</span>
            <h2>{images.length}</h2>
          </div>
        </div>
      </header>

      <div className="admin-content">
        <div className="content-grid">
          <section className="folder-stats-section">
            <h2 className="section-title">Collections</h2>
            <div className="folder-stats-grid">
              {folderStats.map((f, i) => (
                <div className="folder-stat-card" key={i}>
                  <div className="folder-icon" style={{ backgroundColor: getFolderColor(f.name) }}>
                    {f.name.charAt(0)}
                  </div>
                  <div className="folder-info">
                    <h3>{f.name}</h3>
                    <p>{f.count} images</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="upload-section">
            <div className="upload-card">
              <h2 className="section-title">Upload New Images</h2>
              <div className="upload-controls">
                <div className="custom-select-wrapper">
                  <select 
                    value={selectedFolder} 
                    onChange={e => setSelectedFolder(e.target.value)}
                    className="premium-select"
                  >
                    {folders.map((folder, idx) => (
                      <option key={idx} value={folder}>{folder}</option>
                    ))}
                  </select>
                  <div className="select-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M6 9l6 6 6-6"></path>
                    </svg>
                  </div>
                </div>
                
                <label className="premium-upload-button">
                  <input 
                    type="file" 
                    accept="image/*" 
                    multiple 
                    onChange={handleImageUpload} 
                    disabled={isUploading}
                  />
                  <span className="button-content">
                    {isUploading ? (
                      <>
                        <svg className="spinner" width="18" height="18" viewBox="0 0 24 24">
                          <path d="M12 2v4m0 12v4m6-10h4M2 12h4m13.364-5.364l-2.828 2.828m-9.9 9.9l-2.828 2.828" />
                        </svg>
                        Uploading ({uploadProgress}%)
                      </>
                    ) : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"></path>
                        </svg>
                        Select Images
                      </>
                    )}
                  </span>
                </label>
              </div>
              {isUploading && (
                <div className="upload-progress">
                  <div 
                    className="progress-bar" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              )}
            </div>
          </section>
        </div>

        <section className="gallery-section">
          <div className="gallery-header">
  <div className="gallery-header-row">
    <h2 className="section-title">Image Gallery</h2>
  </div>
  <div className="gallery-header-row">
    <div className="custom-select-wrapper">
      <select 
        value={filterFolder} 
        onChange={e => setFilterFolder(e.target.value)}
        className="premium-select"
      >
        {filterOptions.map((folder, idx) => (
          <option key={idx} value={folder}>{folder}</option>
        ))}
      </select>
      <div className="select-icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M6 9l6 6 6-6"></path>
        </svg>
      </div>
    </div>
  </div>
</div>

          {filteredImages.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h3>No images found</h3>
              <p>Upload some images to get started</p>
            </div>
          ) : (
            <div className="masonry-gallery">
              {filteredImages.map((img, idx) => (
                <div key={idx} className="gallery-item">
                  <div className="image-container">
                    <img 
                      src={`${IMG_BASE}${img.url}`} 
                      alt={`Gallery item ${idx}`}
                      loading="lazy"
                    />
                    <div className="image-overlay">
                      <button 
                        className="delete-button"
                        onClick={() => handleDeleteImage(img.url)}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="image-meta">
                    <span className="folder-tag" style={{ backgroundColor: getFolderColor(img.folder) }}>
                      {img.folder}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

// Helper function for folder colors
function getFolderColor(folder) {
  const colors = {
    'Weddings': '#FF6B6B',
    'Events': '#4ECDC4',
    'Concerts': '#FFA07A',
    'Portraits': '#A0E7E5',
    'Pre-Shoots': '#B5EAD7'
  };
  return colors[folder] || '#C7CEEA';
}

export default PhotoAdmin;